// src/cockpit4/components/FiveLayerPlanDrilldown.tsx
// ============================================
// 五层计划联动展示（年→季→月→周→日）
//
// 白皮书第二章：战略计划闭环
//   年度 North Star → 季度 OKR → 月度主题 → 周冲刺 → 日执行
//
// 核心要求（用户反馈）：
//   "一个页面里能同时看到今天这件事，对应本周哪个目标，
//    本周目标又服务于本季度哪个方向"
//   "年度目标点开能看到它拆解出的季度目标，
//    季度目标点开能看到拆解出的月度/周计划，逐层下钻"
//
// 布局：左中右三栏联动
//   左栏：年度目标列表（点击选中 → 联动中间栏）
//   中栏：选中年度下的季度 → 月度 → 周计划（瀑布流下钻）
//   右栏：选中周/日的具体任务清单
//
// 数据来源：Obsidian Vault
//   - 年度：11_Strategy（战略层）/年度目标-{year}.md
//   - 季度：11_Strategy（战略层）/Q{N}-{year}.md
//   - 月度：10_Operations（运营层）/Monthly/{year}-{month}.md
//   - 周度：10_Operations（运营层）/Weekly/W{week}.md
//   - 日度：10_Operations（运营层）/Daily/{date}.md
//
// 铁律一：无数据显示"暂无计划"，不展示假数据
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  ChevronRight,
  Target,
  Flag,
  Circle,
  CheckCircle2,
  CircleDashed,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  ArrowDown,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

type PlanLevel = "annual" | "quarterly" | "monthly" | "weekly" | "daily";

interface PlanItem {
  title: string;
  status: "completed" | "in_progress" | "pending" | "at_risk";
}

interface PlanNode {
  level: PlanLevel;
  id: string;
  title: string;
  progress: number;
  status: "completed" | "in_progress" | "pending" | "at_risk" | "unknown";
  items: PlanItem[];
  vaultPath?: string;
  summary?: string;
}

// ============================================
// 层级配置
// ============================================

const LEVEL_CONFIG: Record<PlanLevel, { label: string; color: string; icon: typeof Calendar }> = {
  annual: { label: "年度", color: "#F59E0B", icon: Target },
  quarterly: { label: "季度", color: "#8B5CF6", icon: Flag },
  monthly: { label: "月度", color: "#3B82F6", icon: Calendar },
  weekly: { label: "周度", color: "#10B981", icon: CircleDashed },
  daily: { label: "日度", color: "#EC4899", icon: CheckCircle2 },
};

// ============================================
// 主组件 - 五层联动展示
// ============================================

export default function FiveLayerPlanDrilldown() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 五层数据
  const [annualPlans, setAnnualPlans] = useState<PlanNode[]>([]);
  const [quarterlyPlans, setQuarterlyPlans] = useState<PlanNode[]>([]);
  const [monthlyPlans, setMonthlyPlans] = useState<PlanNode[]>([]);
  const [weeklyPlans, setWeeklyPlans] = useState<PlanNode[]>([]);
  const [dailyPlans, setDailyPlans] = useState<PlanNode[]>([]);

  // 选中项（联动核心）
  const [selectedAnnual, setSelectedAnnual] = useState<string | null>(null);
  const [selectedQuarterly, setSelectedQuarterly] = useState<string | null>(null);
  const [selectedMonthly, setSelectedMonthly] = useState<string | null>(null);
  const [selectedWeekly, setSelectedWeekly] = useState<string | null>(null);
  const [selectedDaily, setSelectedDaily] = useState<string | null>(null);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);
  const currentWeek = getISOWeek(now);
  const todayStr = now.toISOString().slice(0, 10);

  /** 加载全部五层数据 */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 并行加载所有层级
      const [annual, quarterly, monthly, weekly, daily] = await Promise.all([
        loadAnnualPlan(currentYear),
        loadQuarterlyPlans(currentYear),
        loadMonthlyPlans(currentYear, currentQuarter),
        loadWeeklyPlans(currentYear, currentMonth),
        loadDailyPlans(currentWeek),
      ]);

      setAnnualPlans([annual]);
      setQuarterlyPlans(quarterly);
      setMonthlyPlans(monthly);
      setWeeklyPlans(weekly);
      setDailyPlans(daily);

      // 默认选中当前时间对应的各层
      setSelectedAnnual(annual.id);
      setSelectedQuarterly(`Q${currentQuarter}`);
      const mid = `${currentYear}-${String(currentMonth).padStart(2, "0")}`;
      setSelectedMonthly(mid);
      setSelectedWeekly(`W${currentWeek}`);
      setSelectedDaily(todayStr);
    } catch (err: any) {
      setError(err?.message || "加载计划失败");
    } finally {
      setLoading(false);
    }
  }, [currentYear, currentQuarter, currentMonth, currentWeek, todayStr]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // 联动过滤：选中年度 → 过滤季度；选中季度 → 过滤月度；以此类推
  // 每层依赖上层选中，未选上层时下层为空（引导用户逐层下钻）
  const filteredQuarterly = selectedAnnual ? quarterlyPlans : [];
  const filteredMonthly = selectedQuarterly
    ? monthlyPlans.filter((m) => {
        const q = parseInt(selectedQuarterly.replace("Q", ""));
        const mMonth = parseInt(m.id.split("-")[1]);
        return getQuarterMonths(String(q)).includes(mMonth);
      })
    : [];
  // 周度：选中月度后才显示（按月过滤周）
  const filteredWeekly = selectedMonthly
    ? weeklyPlans.filter((w) => {
        // 周 id 格式 W{N}，通过 ISO 周计算所属月份
        const weekNum = parseInt(w.id.replace("W", ""));
        const weekDate = getWeekDate(currentYear, weekNum);
        const weekMonth = weekDate.getMonth() + 1;
        const selectedMonth = parseInt(selectedMonthly.split("-")[1]);
        return weekMonth === selectedMonth;
      })
    : [];
  // 日度：选中周度后才显示（按周过滤日）
  const filteredDaily = selectedWeekly
    ? dailyPlans.filter((d) => {
        // 日 id 格式 YYYY-MM-DD，通过 ISO 周判断属于哪一周
        const date = new Date(d.id);
        const weekNum = getISOWeek(date);
        const selectedWeekNum = parseInt(selectedWeekly.replace("W", ""));
        return weekNum === selectedWeekNum;
      })
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-amber-400" />
          <span className="text-[13px] font-medium text-white">五层计划联动</span>
          <span className="text-[9px] font-mono text-gray-600">年→季→月→周→日</span>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          title="刷新"
          className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={11} className="animate-spin text-amber-400" />
          ) : (
            <RefreshCw size={11} className="text-amber-400" />
          )}
          <span className="text-[10px] font-mono text-amber-300">刷新</span>
        </button>
      </div>

      {/* 内容区：五层瀑布流联动 */}
      <div className="p-3 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-[12px] text-gray-500">
            <Loader2 size={14} className="animate-spin mr-2" /> 加载五层计划...
          </div>
        ) : error ? (
          <div className="text-[12px] text-red-400 py-4 text-center">加载失败：{error}</div>
        ) : (
          <div className="space-y-2">
            {/* 联动链路提示 */}
            <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-white/[0.02] border border-white/[0.04] text-[10px] font-mono text-gray-500">
              <Sparkles size={10} className="text-amber-400" />
              <span>联动链路：</span>
              <span className="text-amber-400">{selectedAnnual || "—"}</span>
              <ArrowDown size={9} className="text-gray-600 rotate-[-90deg]" />
              <span className="text-violet-400">{selectedQuarterly || "—"}</span>
              <ArrowDown size={9} className="text-gray-600 rotate-[-90deg]" />
              <span className="text-blue-400">{selectedMonthly || "—"}</span>
              <ArrowDown size={9} className="text-gray-600 rotate-[-90deg]" />
              <span className="text-emerald-400">{selectedWeekly || "—"}</span>
              <ArrowDown size={9} className="text-gray-600 rotate-[-90deg]" />
              <span className="text-pink-400">{selectedDaily || "—"}</span>
            </div>

            {/* 五层水平展示 */}
            <div className="grid grid-cols-5 gap-2 min-h-[400px]">
              {/* 第1层：年度 */}
              <LayerColumn
                level="annual"
                nodes={annualPlans}
                selectedId={selectedAnnual}
                onSelect={(id) => {
                  setSelectedAnnual(id);
                  // 联动重置下层
                  setSelectedQuarterly(null);
                  setSelectedMonthly(null);
                  setSelectedWeekly(null);
                  setSelectedDaily(null);
                }}
              />

              {/* 第2层：季度 */}
              <LayerColumn
                level="quarterly"
                nodes={filteredQuarterly}
                selectedId={selectedQuarterly}
                onSelect={(id) => {
                  setSelectedQuarterly(id);
                  setSelectedMonthly(null);
                  setSelectedWeekly(null);
                  setSelectedDaily(null);
                }}
                emptyHint={selectedAnnual ? "暂无季度计划" : "← 先选年度"}
              />

              {/* 第3层：月度 */}
              <LayerColumn
                level="monthly"
                nodes={filteredMonthly}
                selectedId={selectedMonthly}
                onSelect={(id) => {
                  setSelectedMonthly(id);
                  setSelectedWeekly(null);
                  setSelectedDaily(null);
                }}
                emptyHint={selectedQuarterly ? "暂无月度计划" : "← 先选季度"}
              />

              {/* 第4层：周度 */}
              <LayerColumn
                level="weekly"
                nodes={filteredWeekly}
                selectedId={selectedWeekly}
                onSelect={(id) => {
                  setSelectedWeekly(id);
                  setSelectedDaily(null);
                }}
                emptyHint={selectedMonthly ? "暂无周计划" : "← 先选月度"}
              />

              {/* 第5层：日度 */}
              <LayerColumn
                level="daily"
                nodes={filteredDaily}
                selectedId={selectedDaily}
                onSelect={(id) => setSelectedDaily(id)}
                emptyHint={selectedWeekly ? "暂无日计划" : "← 先选周度"}
              />
            </div>

            {/* 选中节点的详情面板 */}
            <SelectedDetailPanel
              annual={annualPlans.find((a) => a.id === selectedAnnual)}
              quarterly={filteredQuarterly.find((q) => q.id === selectedQuarterly)}
              monthly={filteredMonthly.find((m) => m.id === selectedMonthly)}
              weekly={filteredWeekly.find((w) => w.id === selectedWeekly)}
              daily={filteredDaily.find((d) => d.id === selectedDaily)}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：单层列（展示该层所有节点，可选中）
// ============================================

function LayerColumn({
  level,
  nodes,
  selectedId,
  onSelect,
  emptyHint,
}: {
  level: PlanLevel;
  nodes: PlanNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  emptyHint?: string;
}) {
  const cfg = LEVEL_CONFIG[level];
  const Icon = cfg.icon;

  return (
    <div className="flex flex-col rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
      {/* 层级头 */}
      <div
        className="flex items-center gap-1.5 px-2 py-1.5 border-b border-white/[0.04]"
        style={{ background: `${cfg.color}08` }}
      >
        <Icon size={11} style={{ color: cfg.color }} />
        <span className="text-[10px] font-mono font-medium" style={{ color: cfg.color }}>
          {cfg.label}
        </span>
        <span className="text-[9px] text-gray-600 ml-auto">{nodes.length}</span>
      </div>

      {/* 节点列表 */}
      <div className="flex-1 overflow-y-auto p-1.5 space-y-1 min-h-[200px]">
        {nodes.length === 0 ? (
          <div className="text-[10px] text-gray-600 italic text-center py-4">
            {emptyHint || "暂无数据"}
          </div>
        ) : (
          nodes.map((node) => {
            const isSelected = node.id === selectedId;
            return (
              <motion.button
                key={node.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelect(node.id)}
                className={`w-full text-left rounded-md border p-1.5 transition-colors ${
                  isSelected
                    ? "bg-white/[0.06] border-white/[0.15]"
                    : "bg-white/[0.01] border-white/[0.04] hover:bg-white/[0.03]"
                }`}
                style={isSelected ? { borderColor: `${cfg.color}40`, background: `${cfg.color}10` } : {}}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-[10px] font-medium truncate ${isSelected ? "text-white" : "text-gray-300"}`}>
                    {node.title}
                  </span>
                  <span className="text-[9px] font-mono flex-shrink-0" style={{ color: cfg.color }}>
                    {node.progress}%
                  </span>
                </div>
                {/* 迷你进度条 */}
                <div className="h-0.5 bg-white/[0.04] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${node.progress}%`, background: cfg.color }}
                  />
                </div>
                {/* 状态图标 */}
                <div className="flex items-center gap-1 mt-0.5">
                  {node.items.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="w-1 h-1 rounded-full" style={{
                      background: item.status === "completed" ? "#34D399" :
                        item.status === "in_progress" ? "#FBBF24" :
                        item.status === "at_risk" ? "#F87171" : "#4B5563"
                    }} />
                  ))}
                  {node.items.length > 3 && (
                    <span className="text-[8px] text-gray-600">+{node.items.length - 3}</span>
                  )}
                </div>
              </motion.button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============================================
// 子组件：选中节点的详情面板（显示联动关系）
// ============================================

function SelectedDetailPanel({
  annual,
  quarterly,
  monthly,
  weekly,
  daily,
}: {
  annual?: PlanNode;
  quarterly?: PlanNode;
  monthly?: PlanNode;
  weekly?: PlanNode;
  daily?: PlanNode;
}) {
  const hasAny = annual || quarterly || monthly || weekly || daily;
  if (!hasAny) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={11} className="text-amber-400" />
        <span className="text-[11px] font-medium text-white">联动详情：今日任务如何服务于年度目标</span>
      </div>

      {/* 联动链路可视化 */}
      <div className="flex items-center gap-1 mb-3 flex-wrap text-[10px] font-mono">
        {daily && (
          <span className="px-1.5 py-0.5 rounded bg-pink-500/10 text-pink-400 border border-pink-500/20">
            📅 {daily.title}：{daily.items.length} 项任务
          </span>
        )}
        {weekly && (
          <>
            <ChevronRight size={10} className="text-gray-600" />
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              📋 {weekly.title}：{weekly.items.length} 项目标
            </span>
          </>
        )}
        {monthly && (
          <>
            <ChevronRight size={10} className="text-gray-600" />
            <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
              📊 {monthly.title}：{monthly.items.length} 项主题
            </span>
          </>
        )}
        {quarterly && (
          <>
            <ChevronRight size={10} className="text-gray-600" />
            <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 border border-violet-500/20">
              🚩 {quarterly.title}：{quarterly.items.length} 项 OKR
            </span>
          </>
        )}
        {annual && (
          <>
            <ChevronRight size={10} className="text-gray-600" />
            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
              🎯 {annual.title}：{annual.items.length} 项 North Star
            </span>
          </>
        )}
      </div>

      {/* 今日任务清单（最详细） */}
      {daily && daily.items.length > 0 ? (
        <div className="space-y-1">
          <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-1">
            今日任务清单（{daily.items.filter((i) => i.status === "completed").length}/{daily.items.length} 完成）
          </div>
          {daily.items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-[11px]">
              {item.status === "completed" ? (
                <CheckCircle2 size={10} className="text-emerald-400 flex-shrink-0" />
              ) : item.status === "in_progress" ? (
                <CircleDashed size={10} className="text-amber-400 flex-shrink-0" />
              ) : item.status === "at_risk" ? (
                <AlertTriangle size={10} className="text-red-400 flex-shrink-0" />
              ) : (
                <Circle size={10} className="text-gray-600 flex-shrink-0" />
              )}
              <span className={item.status === "completed" ? "text-gray-500 line-through" : "text-gray-200"}>
                {item.title}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-gray-600 italic">
          今日暂无具体任务。点击 CEO 面板的"生成今日计划"按钮可自动生成。
        </div>
      )}
    </motion.div>
  );
}

// ============================================
// 数据加载函数（从 Obsidian Vault 读取）
// 铁律一：读取失败返回空节点，不返回假数据
// ============================================

function parseFrontmatter(content: string): Record<string, any> {
  const fmMatch = content.match(/^---\n([\s\S]*?)\n---/);
  if (!fmMatch) return {};
  const fm: Record<string, any> = {};
  for (const line of fmMatch[1].split("\n")) {
    const m = line.match(/^(\w+):\s*(.*)$/);
    if (m) {
      let val: any = m[2].trim();
      if (val === "true") val = true;
      else if (val === "false") val = false;
      else if (/^\d+$/.test(val)) val = parseInt(val);
      else if (/^"(.*)"$/.test(val)) val = val.slice(1, -1);
      fm[m[1]] = val;
    }
  }
  return fm;
}

function parseTaskItems(content: string): PlanItem[] {
  const items: PlanItem[] = [];
  for (const line of content.split("\n")) {
    const m = line.match(/^- \[([ x])\]\s+(.+)/);
    if (m) {
      items.push({
        title: m[2].trim(),
        status: m[1] === "x" ? "completed" : "pending",
      });
    }
  }
  return items;
}

async function readVaultFile(path: string): Promise<string | null> {
  try {
    const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data?.success || typeof data.content !== "string") return null;
    return data.content;
  } catch {
    return null;
  }
}

function calcProgress(items: PlanItem[]): number {
  if (items.length === 0) return 0;
  const done = items.filter((i) => i.status === "completed").length;
  return Math.round((done / items.length) * 100);
}

async function loadAnnualPlan(year: number): Promise<PlanNode> {
  const paths = [
    `11_Strategy（战略层）/年度目标-${year}.md`,
    `11_Strategy（战略层）/年度目标.md`,
    `11_Strategy/年度目标-${year}.md`,
  ];
  for (const p of paths) {
    const content = await readVaultFile(p);
    if (content) {
      const fm = parseFrontmatter(content);
      const items = parseTaskItems(content);
      return {
        level: "annual",
        id: String(year),
        title: `${year} 年度目标`,
        progress: fm.progress || calcProgress(items),
        status: fm.status || "in_progress",
        items,
        vaultPath: p,
        summary: fm.vision || fm.summary,
      };
    }
  }
  return {
    level: "annual",
    id: String(year),
    title: `${year} 年度目标`,
    progress: 0,
    status: "pending",
    items: [],
  };
}

async function loadQuarterlyPlan(year: number, qid: string): Promise<PlanNode> {
  const paths = [
    `11_Strategy（战略层）/${qid}-${year}.md`,
    `11_Strategy（战略层）/${qid}.md`,
    `10_Operations（运营层）/Quarterly/${qid}-${year}.md`,
  ];
  for (const p of paths) {
    const content = await readVaultFile(p);
    if (content) {
      const fm = parseFrontmatter(content);
      const items = parseTaskItems(content);
      return {
        level: "quarterly",
        id: qid,
        title: `${qid} ${year}`,
        progress: fm.progress || calcProgress(items),
        status: fm.status || "in_progress",
        items,
        vaultPath: p,
        summary: fm.focus,
      };
    }
  }
  return {
    level: "quarterly",
    id: qid,
    title: `${qid} ${year}`,
    progress: 0,
    status: "pending",
    items: [],
  };
}

async function loadQuarterlyPlans(year: number): Promise<PlanNode[]> {
  const nodes: PlanNode[] = [];
  for (let q = 1; q <= 4; q++) {
    nodes.push(await loadQuarterlyPlan(year, `Q${q}`));
  }
  return nodes;
}

async function loadMonthlyPlan(mid: string): Promise<PlanNode> {
  const paths = [
    `10_Operations（运营层）/Monthly/${mid}.md`,
    `10_Operations（运营层）/Monthly/${mid}-计划.md`,
  ];
  for (const p of paths) {
    const content = await readVaultFile(p);
    if (content) {
      const fm = parseFrontmatter(content);
      const items = parseTaskItems(content);
      const [y, m] = mid.split("-");
      return {
        level: "monthly",
        id: mid,
        title: `${y}年${parseInt(m)}月`,
        progress: fm.progress || calcProgress(items),
        status: fm.status || "in_progress",
        items,
        vaultPath: p,
        summary: fm.theme || fm.focus,
      };
    }
  }
  const [y, m] = mid.split("-");
  return {
    level: "monthly",
    id: mid,
    title: `${y}年${parseInt(m)}月`,
    progress: 0,
    status: "pending",
    items: [],
  };
}

async function loadMonthlyPlans(year: number, quarter: number): Promise<PlanNode[]> {
  const months = getQuarterMonths(String(quarter));
  const nodes: PlanNode[] = [];
  for (const m of months) {
    const mid = `${year}-${String(m).padStart(2, "0")}`;
    nodes.push(await loadMonthlyPlan(mid));
  }
  return nodes;
}

async function loadWeeklyPlan(wid: string): Promise<PlanNode> {
  const paths = [
    `10_Operations（运营层）/Weekly/${wid}.md`,
    `10_Operations（运营层）/Weekly/第${wid.replace("W", "")}周.md`,
  ];
  for (const p of paths) {
    const content = await readVaultFile(p);
    if (content) {
      const fm = parseFrontmatter(content);
      const items = parseTaskItems(content);
      return {
        level: "weekly",
        id: wid,
        title: `${wid} 周`,
        progress: fm.progress || calcProgress(items),
        status: fm.status || "in_progress",
        items,
        vaultPath: p,
        summary: fm.focus,
      };
    }
  }
  return {
    level: "weekly",
    id: wid,
    title: `${wid} 周`,
    progress: 0,
    status: "pending",
    items: [],
  };
}

async function loadWeeklyPlans(year: number, month: number): Promise<PlanNode[]> {
  const nodes: PlanNode[] = [];
  for (let w = 1; w <= 5; w++) {
    const node = await loadWeeklyPlan(`W${w}`);
    if (node.items.length > 0 || node.status !== "pending") {
      nodes.push(node);
    }
  }
  return nodes;
}

async function loadDailyPlan(did: string): Promise<PlanNode> {
  const path = `10_Operations（运营层）/Daily/${did}.md`;
  const content = await readVaultFile(path);
  if (content) {
    const fm = parseFrontmatter(content);
    const items = parseTaskItems(content);
    return {
      level: "daily",
      id: did,
      title: did,
      progress: fm.progress || calcProgress(items),
      status: fm.status || "in_progress",
      items,
      vaultPath: path,
      summary: fm.summary,
    };
  }
  return {
    level: "daily",
    id: did,
    title: did,
    progress: 0,
    status: "pending",
    items: [],
  };
}

async function loadDailyPlans(week: number): Promise<PlanNode[]> {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7;
  const monday = new Date(now);
  monday.setDate(now.getDate() - dayOfWeek + 1);
  const nodes: PlanNode[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const did = d.toISOString().slice(0, 10);
    nodes.push(await loadDailyPlan(did));
  }
  return nodes;
}

// ============================================
// 工具函数
// ============================================

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function getQuarterMonths(qNum: string): number[] {
  const q = parseInt(qNum);
  const start = (q - 1) * 3 + 1;
  return [start, start + 1, start + 2];
}

/** 根据年份 + ISO 周号，计算该周的周一日期（用于判断周属于哪个月） */
function getWeekDate(year: number, weekNum: number): Date {
  const jan1 = new Date(year, 0, 1);
  const dayOfWeek = jan1.getDay() || 7;
  const monday = new Date(jan1);
  monday.setDate(jan1.getDate() - dayOfWeek + 1 + (weekNum - 1) * 7);
  return monday;
}
